import { useRef, useState } from 'react';
import styled from 'styled-components';
import type { FinPaymentRequestRow } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Dialog } from '@shared/ui/Dialog';
import { TextArea } from '@shared/ui/Input';
import { formatMoney } from '../financierFormat';

const Summary = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Caption = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ErrorText = styled.div`
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.status.danger};
`;

export interface ReasonTarget {
  req: FinPaymentRequestRow;
  decision: 'deferred' | 'rejected';
}

interface Props {
  target: ReasonTarget;
  onClose: () => void;
  /** Решение с причиной; вернуть текст ошибки — диалог останется открытым. */
  onConfirm: (reason: string) => Promise<string | null>;
}

/**
 * Причина отказа или отсрочки. Её сохраняет решение (`comment`), и по заявке
 * сотрудника («Заявки на оплату») он видит её в своей карточке — там причина
 * обязательна: без неё сотруднику не понять, что исправить.
 */
export function DecisionReasonDialog({ target, onClose, onConfirm }: Props) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const { req, decision } = target;
  const staff = Boolean(req.staff_request_id);
  const rejecting = decision === 'rejected';

  async function submit() {
    const text = reason.trim();
    if (staff && text.length < 3) {
      setError('Напишите причину — сотрудник увидит её в своей заявке.');
      return;
    }
    setBusy(true);
    const failed = await onConfirm(text);
    setBusy(false);
    if (failed) setError(failed);
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      title={
        rejecting
          ? `Отклонить заявку ${req.request_number}?`
          : `Отложить заявку ${req.request_number}?`
      }
      initialFocusRef={fieldRef}
      footer={
        <>
          <Button type="button" $variant="secondary" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button
            type="button"
            $variant={rejecting ? 'danger' : 'primary'}
            onClick={() => void submit()}
            disabled={busy}
          >
            {busy ? 'Сохраняем…' : rejecting ? 'Отклонить' : 'Отложить'}
          </Button>
        </>
      }
    >
      <Summary>
        {req.counterparty_name} · {formatMoney(req.requested_amount || req.amount)}
      </Summary>
      <Caption htmlFor="fin-decision-reason">
        {staff ? 'Причина (её увидит сотрудник)' : 'Причина (необязательно)'}
      </Caption>
      <TextArea
        ref={fieldRef}
        id="fin-decision-reason"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
          setError(null);
        }}
        placeholder={
          rejecting
            ? 'Например: счёт выставлен не на нашу организацию — попросите поставщика переделать'
            : 'Например: оплатим после поступления от Заказчика, ориентир — 15.11'
        }
        rows={3}
        maxLength={1000}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? 'fin-decision-reason-error' : undefined}
      />
      {error && (
        <ErrorText id="fin-decision-reason-error" role="alert">
          {error}
        </ErrorText>
      )}
    </Dialog>
  );
}
